/**
 * DiscussMessageInput — minimal textarea + send button for the Discuss
 * modal.
 *
 * Differs from `features/session/components/MessageInput.tsx`:
 *
 *   - No agent dropdown lives here (the parent modal controls which agent
 *     the input addresses via the `agentType` prop).
 *   - No draft-per-agent persistence (debugging conversations are
 *     ephemeral enough that an in-memory draft is fine).
 *   - No file attachments, no speech input — keeps the surface small;
 *     these can be added later if operators ask.
 *   - Manages SSE streaming against `useDiscussStore` (keyed by the
 *     compound `(executionId, agentType)` pair, per Task 3701h G.1),
 *     NOT the singleton session store.
 *   - On the first send for a given (execution, agentType), physically
 *     concatenates the markdown context blob in front of the operator's
 *     text, wrapped in `<execution_context>...</execution_context>`
 *     delimiters. This is the only way to ship context to the agent
 *     because `/v1/agents/{id}/stream` accepts exactly one `new_message`
 *     per call (Q2 of the design validation).
 *
 * Imperative handle (Task 3701h G.2): the parent `DiscussExecutionModal`
 * exposes a "Restart conversation" button in the header. The button
 * needs to abort any in-flight SSE stream owned by this input and focus
 * the textarea afterwards. Those side effects live in this component's
 * refs, so we expose them via `useImperativeHandle` rather than lifting
 * them into the parent.
 *
 * The SSE event reducer mirrors the one in `MessageInput.tsx` so future
 * maintenance can copy fixes across both surfaces unchanged.
 */

import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type CSSProperties,
  type FormEvent,
  type KeyboardEvent,
} from 'react'
import { Send, Loader2 } from 'lucide-react'

import { Button } from '@/shared/components/ui/button'
import { useToast } from '@/shared/hooks'
import { sendMessageStream } from '@/features/session/api'
import { generateSessionId } from '@/features/session/store'
import type {
  Message,
  ToolCall,
  ToolResult,
} from '@/features/session/types'
import {
  useDiscussStore,
  selectMessages,
  selectStreamingState,
  selectContextSent,
  type AgentType,
} from '@/features/queue/stores/discuss-store'

/**
 * Diagnostic logging gate for Task 3701h G.3 investigation. When
 * `VITE_DISCUSS_DEBUG_LOG=true` is set in `apps/stream/.env.local` the
 * component emits structured console logs at key points in the
 * send/receive pipeline so post-hoc reproduction of "second turn freeze"
 * style bugs is possible without redeploying. Defaults off so production
 * builds carry no noise.
 */
const DISCUSS_DEBUG = import.meta.env?.VITE_DISCUSS_DEBUG_LOG === 'true'

function debugLog(scope: string, payload: Record<string, unknown>): void {
  if (!DISCUSS_DEBUG) return
  // eslint-disable-next-line no-console
  console.log('[discuss-debug]', scope, payload)
}

/**
 * Public imperative handle exposed to the parent modal so it can drive
 * stream-lifecycle side effects (abort, focus) without lifting refs out
 * of this component. Used by the Restart-conversation affordance.
 */
export interface DiscussMessageInputHandle {
  /** Abort any in-flight SSE stream for the current (executionId,
   * agentType) pair. No-op if nothing is streaming. */
  abortStream: () => void
  /** Focus the message textarea. */
  focus: () => void
}

export interface DiscussMessageInputProps {
  executionId: string
  /** Which agent the input addresses. The store slice for streaming
   * state, messages, sessionId, etc. is keyed by `(executionId,
   * agentType)`, so this prop must match the agent the parent modal is
   * showing in the dropdown. */
  agentType: AgentType
  debugAgentId: string
  /**
   * Markdown context blob (with `<execution_context>` envelope already
   * applied by `buildDiscussSystemMessage`). Required to send — the input
   * is disabled until the parent has finished assembling it.
   *
   * `null` while the parent is still loading the execution / transcripts.
   * `undefined` is treated identically.
   */
  contextText: string | null
  /** Operator user id forwarded to `sendMessageStream` as user_id. */
  operatorId?: string
  /** When true, disable the send affordance (e.g. modal is closing). */
  disabled?: boolean
}

export const DiscussMessageInput = forwardRef<
  DiscussMessageInputHandle,
  DiscussMessageInputProps
>(function DiscussMessageInput(
  { executionId, agentType, debugAgentId, contextText, operatorId, disabled },
  ref,
) {
  const [text, setText] = useState('')
  const { toast } = useToast()
  const abortRef = useRef<(() => void) | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)

  // Store actions
  const addMessage = useDiscussStore((s) => s.addMessage)
  const updateMessage = useDiscussStore((s) => s.updateMessage)
  const appendStreamingContent = useDiscussStore((s) => s.appendStreamingContent)
  const addToolCall = useDiscussStore((s) => s.addToolCall)
  const addToolResult = useDiscussStore((s) => s.addToolResult)
  const setStreamingState = useDiscussStore((s) => s.setStreamingState)
  const setError = useDiscussStore((s) => s.setError)
  const markContextSent = useDiscussStore((s) => s.markContextSent)
  const setSessionIdForExecution = useDiscussStore((s) => s.setSessionIdForExecution)
  const getSessionId = useDiscussStore((s) => s.getSessionId)

  // Per-(execution, agentType) slices
  const streamingState = useDiscussStore((s) =>
    selectStreamingState(s, executionId, agentType),
  )
  const contextSent = useDiscussStore((s) =>
    selectContextSent(s, executionId, agentType),
  )

  // Expose imperative controls to the parent modal so its Restart button
  // can abort the in-flight stream and focus the textarea after clearing.
  useImperativeHandle(
    ref,
    () => ({
      abortStream: () => {
        if (abortRef.current) {
          try {
            abortRef.current()
          } catch {
            // best-effort cleanup; ignore
          }
          abortRef.current = null
        }
      },
      focus: () => {
        textareaRef.current?.focus()
      },
    }),
    [],
  )

  // Cleanup any in-flight stream on unmount or when the (execution,
  // agentType) pair changes. Without this, an SSE in-flight when the
  // operator closes the modal — or switches agents — would keep ticking
  // and writing to a now-hidden store slice. The dependency list MUST
  // include `agentType` (added in 3701h G.1) — otherwise switching
  // agents mid-stream leaves the prior agent's abort handler dangling.
  useEffect(() => {
    return () => {
      if (abortRef.current) {
        try {
          abortRef.current()
        } catch {
          // best-effort cleanup; ignore
        }
        abortRef.current = null
      }
    }
  }, [executionId, agentType])

  const sendDisabled =
    disabled ||
    streamingState === 'streaming' ||
    text.trim().length === 0 ||
    (!contextSent && !contextText) // first turn needs context

  const handleSend = useCallback(
    (event?: FormEvent | KeyboardEvent) => {
      event?.preventDefault?.()
      const userText = text.trim()
      if (userText.length === 0) return
      if (streamingState === 'streaming') return
      if (!contextSent && !contextText) {
        // First turn requires the context blob to be ready.
        toast({
          title: 'Context still loading',
          description:
            'Wait a moment for the execution context to finish loading, then try again.',
        })
        return
      }

      // Resume an existing session if one was persisted for this
      // (execution, agentType) pair. Otherwise mint a new one —
      // `sendMessageStream` also generates if we pass null, but we want
      // to persist immediately so a reload mid-stream can resume.
      const persistedSessionId = getSessionId(executionId, agentType)
      const sessionId = persistedSessionId ?? generateSessionId()
      setSessionIdForExecution(executionId, agentType, sessionId)
      debugLog('handleSend.sessionAssign', {
        executionId,
        agentType,
        sessionId,
        wasPersisted: persistedSessionId !== undefined,
        contextSent,
      })

      // Build the payload. On first turn for this (execution, agentType),
      // prepend the context envelope; otherwise ship the bare message.
      const payloadText = contextSent
        ? userText
        : `${contextText}\n\n---\n\n${userText}`

      // User-facing bubble shows only what the operator typed — the
      // envelope is an implementation detail and would otherwise dominate
      // the thread.
      const userMessage: Message = {
        id: `msg_${Date.now()}_u`,
        role: 'user',
        content: userText,
        createdAt: Date.now(),
      }
      addMessage(executionId, agentType, userMessage)
      setText('')

      // Placeholder agent message to stream into. `agentMessageId` is
      // captured by the SSE event reducer below — every reducer call
      // operates on this bound id rather than re-reading from the
      // store, which keeps multi-turn streams safe against the closure
      // staleness pattern flagged in Task 3701h G.3.
      const agentMessageId = `msg_${Date.now()}_a`
      const agentMessage: Message = {
        id: agentMessageId,
        role: 'agent',
        content: '',
        createdAt: Date.now(),
        isStreaming: true,
      }
      addMessage(executionId, agentType, agentMessage)
      setStreamingState(executionId, agentType, 'streaming')

      // Kick the stream. The reducer below mirrors `MessageInput.tsx`'s
      // event handling — keep the two in sync if the backend SSE protocol
      // changes.
      const { abort } = sendMessageStream(
        debugAgentId,
        payloadText,
        sessionId,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (rawEvent: any) => {
          // Classify the event for diagnostic logging (3701h G.3). The
          // shape of the SSE payloads is documented in the comments
          // below — `eventKind` is purely for log readability.
          const eventKind = rawEvent.error
            ? 'error'
            : rawEvent.disposition
              ? 'disposition'
              : rawEvent.final && !rawEvent.parts
                ? 'final'
                : rawEvent.partial
                  ? 'partial'
                  : rawEvent.parts
                    ? 'parts'
                    : 'metadata'
          debugLog('sse.event', {
            executionId,
            agentType,
            sessionId,
            agentMessageId,
            eventKind,
          })

          // Skip metadata-only frames.
          if (rawEvent.session_id && !rawEvent.parts && !rawEvent.event_type) return
          if (rawEvent.cost_metadata) return
          if (rawEvent.task_phase && !rawEvent.parts) return
          if (rawEvent.ui_events && !rawEvent.parts) return
          if (rawEvent.artifacts && !rawEvent.parts) return

          // Errors
          if (rawEvent.error) {
            setStreamingState(executionId, agentType, 'error')
            setError(executionId, agentType, rawEvent.message ?? rawEvent.error)
            updateMessage(executionId, agentType, agentMessageId, {
              isStreaming: false,
              error: rawEvent.message ?? rawEvent.error,
            })
            abortRef.current = null
            return
          }

          // Disposition events (completion signal)
          if (rawEvent.disposition) {
            // `agent_text` carries the COMPLETE response. Only use it as a
            // fallback when nothing was streamed via `partial` frames —
            // otherwise it gets concatenated on top of the already-
            // accumulated streaming content and the response renders
            // twice in one bubble. Mirrors the guard in the reference
            // `MessageInput.tsx` disposition handler.
            const streamedContent = selectMessages(
              useDiscussStore.getState(),
              executionId,
              agentType,
            ).find((m) => m.id === agentMessageId)?.content
            if (rawEvent.agent_text && !streamedContent) {
              appendStreamingContent(
                executionId,
                agentType,
                agentMessageId,
                rawEvent.agent_text,
              )
            }
            updateMessage(executionId, agentType, agentMessageId, {
              isStreaming: false,
            })
            setStreamingState(executionId, agentType, 'idle')
            abortRef.current = null
            return
          }

          // Final marker without parts
          if (rawEvent.final && !rawEvent.parts) {
            updateMessage(executionId, agentType, agentMessageId, {
              isStreaming: false,
            })
            setStreamingState(executionId, agentType, 'idle')
            abortRef.current = null
            return
          }

          // Partial text events (typewriter streaming)
          if (rawEvent.partial && rawEvent.parts) {
            for (const part of rawEvent.parts) {
              if (part.text) {
                appendStreamingContent(
                  executionId,
                  agentType,
                  agentMessageId,
                  part.text,
                )
              }
            }
            return
          }

          // Non-partial events with parts (function_call / function_response)
          if (rawEvent.parts) {
            for (const part of rawEvent.parts) {
              if (part.text) {
                appendStreamingContent(
                  executionId,
                  agentType,
                  agentMessageId,
                  part.text,
                )
              }
              if (part.function_call) {
                const tc: ToolCall = {
                  id: `tc_${Date.now()}_${part.function_call.name}`,
                  name: part.function_call.name,
                  input: part.function_call.args ?? {},
                }
                addToolCall(executionId, agentType, agentMessageId, tc)
              }
              if (part.function_response) {
                const tr: ToolResult = {
                  toolCallId: `tc_${part.function_response.name}`,
                  output: part.function_response.response,
                }
                addToolResult(executionId, agentType, agentMessageId, tr)
              }
            }
            if (rawEvent.final) {
              updateMessage(executionId, agentType, agentMessageId, {
                isStreaming: false,
              })
              setStreamingState(executionId, agentType, 'idle')
              abortRef.current = null
            }
            return
          }
        },
        (err: Error) => {
          debugLog('sse.streamError', {
            executionId,
            agentType,
            sessionId,
            agentMessageId,
            error: err.message,
          })
          toast({
            title: 'Stream error',
            description: err.message,
            variant: 'danger',
          })
          setStreamingState(executionId, agentType, 'error')
          setError(executionId, agentType, err.message)
          updateMessage(executionId, agentType, agentMessageId, {
            isStreaming: false,
            error: err.message,
          })
          abortRef.current = null
        },
        operatorId,
        // onComplete safety-net: ensure streaming state lands at 'idle'
        // even if no explicit terminal frame arrived. Reads the current
        // store state because by the time onComplete fires, the reducer
        // closure above may already have set it — we only force idle if
        // the stream-end happened *without* any terminal frame, which
        // is the freeze symptom Task 3701h G.3 was investigating.
        () => {
          const current = useDiscussStore.getState()
          const stillStreaming =
            current.streamingStateByExecution[executionId]?.[agentType] ===
            'streaming'
          debugLog('sse.onComplete', {
            executionId,
            agentType,
            sessionId,
            agentMessageId,
            stillStreaming,
          })
          if (stillStreaming) {
            updateMessage(executionId, agentType, agentMessageId, {
              isStreaming: false,
            })
            setStreamingState(executionId, agentType, 'idle')
            abortRef.current = null
          }
        },
      )
      abortRef.current = abort

      // Flip contextSent only after the stream has been kicked off so a
      // crash mid-send doesn't strand the modal in "context already sent"
      // for a session that never received it.
      if (!contextSent) {
        markContextSent(executionId, agentType)
        debugLog('handleSend.markContextSent', { executionId, agentType })
      }
    },
    [
      text,
      contextSent,
      contextText,
      executionId,
      agentType,
      debugAgentId,
      streamingState,
      getSessionId,
      setSessionIdForExecution,
      addMessage,
      updateMessage,
      appendStreamingContent,
      addToolCall,
      addToolResult,
      setStreamingState,
      setError,
      markContextSent,
      operatorId,
      toast,
    ],
  )

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLTextAreaElement>) => {
      // Enter to send, Shift+Enter for newline. Matches MessageInput.
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault()
        handleSend(event)
      }
    },
    [handleSend],
  )

  return (
    <form onSubmit={handleSend} style={styles.form} data-testid="discuss-message-input">
      <textarea
        ref={textareaRef}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={
          contextText
            ? 'Ask about this execution…  (Enter to send, Shift+Enter for newline)'
            : 'Loading context…'
        }
        disabled={disabled || !contextText}
        rows={2}
        style={styles.textarea}
        data-testid="discuss-input-textarea"
      />
      <Button
        type="submit"
        variant="default"
        size="icon"
        disabled={sendDisabled}
        aria-label="Send"
        style={styles.sendButton}
      >
        {streamingState === 'streaming' ? (
          <Loader2 size={16} className="animate-spin" />
        ) : (
          <Send size={16} />
        )}
      </Button>
    </form>
  )
})

const styles: Record<string, CSSProperties> = {
  form: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: '8px',
    padding: '12px 16px',
    borderTop: '1px solid var(--border-subtle)',
    background: 'var(--bg-secondary)',
  },
  textarea: {
    flex: 1,
    minHeight: '40px',
    maxHeight: '160px',
    padding: '8px 12px',
    fontSize: '14px',
    color: 'var(--text-primary)',
    background: 'var(--bg-primary)',
    border: '1px solid var(--border-default)',
    borderRadius: 'var(--radius-sm)',
    resize: 'vertical',
    fontFamily: 'inherit',
  },
  sendButton: {
    flexShrink: 0,
  },
}

export default DiscussMessageInput
